import { isFullTestimonialContentBlock, type FullTestimonialContentBlock } from "../types.js";
import { parseTaggedText, type ParsedTaggedText } from "../taggedTextParser.js";
import { sharedSheet } from "../styles/sharedStyles.js";
import componentStylesText from "./TestimonialBlockComponent.css?inline";
import {
    isAllowedPersonImage,
    parseCssDuration,
    tokenizeHighlights,
    type TokenizedHighlightSegment,
    type TokenizedHighlights
} from "./testimonialBlockHelpers.js";
import "./ExpandableBlockComponent.js";

const componentSheet = new CSSStyleSheet();
componentSheet.replaceSync(componentStylesText);

type TestimonialAnimationState = "collapsed" | "expanding" | "expanded" | "collapsing";
type FullTextVisibilityState = "hidden" | "fading-in" | "visible" | "fading-out";
type CollapsedContextVisibilityState = "visible" | "fading-out" | "hidden" | "fading-in";

interface RelativeRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface QuoteStageMeasurement {
    collapsedHeight: number;
    expandedHeight: number;
    collapsedTokenRects: Map<string, RelativeRect>;
    expandedTokenRects: Map<string, RelativeRect>;
    collapsedClosingQuoteRect: RelativeRect;
    expandedClosingQuoteRect: RelativeRect;
}

export interface TestimonialGeometryFallbackProvider {
    measureRelativeRect(element: HTMLElement, index: number, layerState: "collapsed" | "expanded"): RelativeRect;
    measureLayerHeight(layer: HTMLElement | null, fallbackText: string): number;
}

interface TestimonialAnimationTiming {
    quoteStageHeightDurationMs: number;
    highlightedWordMotionDurationMs: number;
    fullTextFadeDurationMs: number;
    closingQuoteMotionDurationMs: number;
    collapsedContextFadeDurationMs: number;
}

const DEFAULT_TIMING: TestimonialAnimationTiming = {
    quoteStageHeightDurationMs: 240,
    highlightedWordMotionDurationMs: 240,
    fullTextFadeDurationMs: 120,
    closingQuoteMotionDurationMs: 240,
    collapsedContextFadeDurationMs: 60
};

/**
 * TestimonialBlockComponent — A leaf component for rendering a testimonial entry in the chat.
 *
 * The component owns the full testimonial quote stage itself. The expandable shell only provides
 * the bubble chrome and toggle button, while this component controls:
 * - collapsed/expanded state transitions
 * - the layered testimonial quote DOM
 * - per-word highlight motion
 * - height choreography and accessibility text timing
 *
 * Attributes:
 * - block: The JSON stringified testimonial block object.
 */
export class TestimonialBlockComponent extends HTMLElement {
    static geometryFallbackProvider: TestimonialGeometryFallbackProvider | null = null;

    private shellExpanded: boolean = false;
    private animationState: TestimonialAnimationState = "collapsed";
    private fullTextVisibility: FullTextVisibilityState = "hidden";
    private collapsedContextVisibility: CollapsedContextVisibilityState = "visible";
    private expandableShell: HTMLElement | null = null;
    private head: HTMLDivElement | null = null;
    private body: HTMLDivElement | null = null;
    private quoteStage: HTMLDivElement | null = null;
    private quoteStageViewport: HTMLDivElement | null = null;
    private highlightRestLayer: HTMLDivElement | null = null;
    private highlightMotionLayer: HTMLDivElement | null = null;
    private fullTextLayer: HTMLDivElement | null = null;
    private semanticText: HTMLParagraphElement | null = null;
    private animationTimerIds: Set<number> = new Set();
    private resizeObserver: ResizeObserver | null = null;
    private resizeFrameId: number | null = null;

    static get observedAttributes(): string[] {
        return ["block", "role"];
    }

    constructor() {
        super();
    }

    /**
     * Returns the current testimonial text that should be aggregated by the parent message list.
     */
    get accessibilityText(): string {
        const data = this.getTestimonialData();
        if (!data) {
            return "";
        }

        try {
            const parsed = parseTaggedText(data.text);
            const visibleText = this.fullTextVisibility === "hidden" ? parsed.collapsedText : parsed.plainText;

            return `Testimonial by ${data.person.name} from ${data.company}: ${visibleText}`;
        } catch {
            return "";
        }
    }

    /**
     * Reads and validates the testimonial block payload from the host attribute.
     */
    private getTestimonialData(): FullTestimonialContentBlock | null {
        const blockAttr = this.getAttribute("block");
        if (!blockAttr) {
            return null;
        }

        try {
            const parsed: unknown = JSON.parse(blockAttr);
            return isFullTestimonialContentBlock(parsed) ? parsed : null;
        } catch {
            return null;
        }
    }

    /**
     * Creates the shadow DOM shell once and then refreshes the testimonial content.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: "open" });
            shadow.adoptedStyleSheets = [sharedSheet, componentSheet];
            this.render();
        }

        this.startResizeObservation();
        this.updateTestimonial();
        this.emitTextUpdate();
    }

    /**
     * Cleans up asynchronous timers and resize listeners when the element leaves the document.
     */
    disconnectedCallback(): void {
        this.clearAnimationTimers();
        this.stopResizeObservation();
    }

    /**
     * Refreshes the rendered testimonial when block data or the speaker role changes.
     */
    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (name === "block") {
            this.clearAnimationTimers();
        }

        if (name === "block" || name === "role") {
            this.updateTestimonial();
            this.emitTextUpdate();
        }
    }

    /**
     * Creates the stable shell DOM. The quote stage is rebuilt whenever testimonial data changes.
     */
    private render(): void {
        const expandable = document.createElement("jt-expandable-block");
        expandable.setAttribute("expand-button-label", "Show full customer reference");
        expandable.setAttribute("collapse-button-label", "Hide full customer reference");
        expandable.addEventListener("toggle-request", (event) => this.handleToggleRequest(event as CustomEvent<{ expanded: boolean }>));

        const root = document.createElement("div");
        root.className = "testimonial-block";

        const head = document.createElement("div");
        head.className = "testimonial-block__head";

        const body = document.createElement("div");
        body.className = "testimonial-block__body";

        root.append(head, body);
        expandable.append(root);

        this.expandableShell = expandable;
        this.head = head;
        this.body = body;

        this.shadowRoot?.appendChild(expandable);
    }

    /**
     * Rebuilds the testimonial DOM from the current block payload and aligns the shell state with it.
     */
    private updateTestimonial(): void {
        if (!this.expandableShell || !this.head || !this.body) {
            return;
        }

        const data = this.getTestimonialData();
        const blockRole = this.getAttribute("role") || "assistant";
        this.expandableShell.setAttribute("role", blockRole);

        if (!data) {
            this.resetVisualStateToCollapsed("visible");
            this.updateShellExpandedState();
            this.head.replaceChildren();
            this.body.replaceChildren();
            this.clearQuoteStageReferences();
            return;
        }

        let parsedText: ParsedTaggedText;
        try {
            parsedText = parseTaggedText(data.text);
        } catch {
            this.resetVisualStateToCollapsed("visible");
            this.updateShellExpandedState();
            this.head.replaceChildren();
            this.body.replaceChildren();
            this.clearQuoteStageReferences();
            return;
        }

        this.renderHead(data);
        this.renderQuoteStage(parsedText);
        this.updateShellExpandedState();
        this.updateQuoteStageStateAttributes(parsedText);
        this.synchronizeSteadyStateLayout(parsedText);
    }

    /**
     * Handles toggle requests from the presentational shell and starts the local testimonial animation.
     */
    private handleToggleRequest(event: CustomEvent<{ expanded: boolean }>): void {
        const data = this.getTestimonialData();
        if (!data || this.isAnimationInProgress()) {
            return;
        }

        let parsedText: ParsedTaggedText;
        try {
            parsedText = parseTaggedText(data.text);
        } catch {
            return;
        }

        const requestedExpanded = event.detail.expanded;
        if (requestedExpanded === this.shellExpanded && !this.isAnimationInProgress()) {
            return;
        }

        if (this.prefersReducedMotion()) {
            this.shellExpanded = requestedExpanded;
            if (requestedExpanded) {
                this.animationState = "expanded";
                this.fullTextVisibility = "visible";
                this.collapsedContextVisibility = "hidden";
            } else {
                this.resetVisualStateToCollapsed("visible");
            }

            this.updateShellExpandedState();
            this.updateQuoteStageStateAttributes(parsedText);
            this.synchronizeSteadyStateLayout(parsedText);
            this.emitTextUpdate();
            return;
        }

        if (requestedExpanded) {
            this.startExpandAnimation(parsedText);
            return;
        }

        this.startCollapseAnimation(parsedText);
    }

    /**
     * Renders the avatar and testimonial metadata above the quote stage.
     */
    private renderHead(data: FullTestimonialContentBlock): void {
        if (!this.head) {
            return;
        }

        this.head.replaceChildren();

        if (isAllowedPersonImage(data.person.image)) {
            const img = document.createElement("img");
            img.src = data.person.image;
            img.alt = "";
            img.className = "testimonial-block__image";
            this.head.appendChild(img);
        }

        const meta = document.createElement("div");
        meta.className = "testimonial-block__meta";

        const name = document.createElement("p");
        name.className = "testimonial-block__name";
        name.textContent = data.person.name;
        meta.appendChild(name);

        const company = document.createElement("p");
        company.className = "testimonial-block__company";
        company.textContent = data.company;
        meta.appendChild(company);

        const role = document.createElement("p");
        role.className = "testimonial-block__role";
        role.textContent = data.person.role;
        meta.appendChild(role);

        this.head.appendChild(meta);
    }

    /**
     * Renders the layered quote-stage DOM that is required for local testimonial animation.
     */
    private renderQuoteStage(parsedText: ParsedTaggedText): void {
        if (!this.body) {
            return;
        }

        const quoteStage = document.createElement("div");
        quoteStage.className = "testimonial-block__quote-stage";

        const openQuote = this.createDecorativeQuoteIcon("testimonial-block__quote testimonial-block__quote--open");

        const viewport = document.createElement("div");
        viewport.className = "testimonial-block__quote-stage-viewport";

        const layers = document.createElement("div");
        layers.className = "testimonial-block__quote-stage-layers";

        const tokenizedHighlights = tokenizeHighlights(parsedText.highlights);
        const highlightRestLayer = this.createHighlightRestLayer(parsedText, tokenizedHighlights);
        const highlightMotionLayer = this.createHighlightMotionLayer(tokenizedHighlights);
        const fullTextLayer = this.createFullTextLayer(parsedText, tokenizedHighlights);

        const semanticText = document.createElement("p");
        semanticText.className = "testimonial-block__semantic-text";

        layers.append(highlightRestLayer, highlightMotionLayer, fullTextLayer);
        viewport.append(layers, semanticText);
        quoteStage.append(openQuote, viewport);

        this.body.replaceChildren(quoteStage);

        this.quoteStage = quoteStage;
        this.quoteStageViewport = viewport;
        this.highlightRestLayer = highlightRestLayer;
        this.highlightMotionLayer = highlightMotionLayer;
        this.fullTextLayer = fullTextLayer;
        this.semanticText = semanticText;
    }

    /**
     * Creates the steady collapsed layer that only shows the highlighted words.
     */
    private createHighlightRestLayer(parsedText: ParsedTaggedText, tokenizedHighlights: TokenizedHighlights): HTMLDivElement {
        const layer = document.createElement("div");
        layer.className = "testimonial-block__quote-stage-layer testimonial-block__quote-stage-layer--highlight-rest";
        layer.setAttribute("aria-hidden", "true");

        const measure = document.createElement("div");
        measure.className = "testimonial-block__quote-stage-measure";

        const copy = document.createElement("p");
        copy.className = "testimonial-block__quote-copy testimonial-block__quote-copy--highlight-rest";
        parsedText.parts.forEach((part, index) => {
            if (part.type === "highlight") {
                const segment = tokenizedHighlights.segments[part.highlightIndex];
                if (segment) {
                    this.appendHighlightTokens(copy, segment, "testimonial-block__highlight-token");
                }
                return;
            }

            const previousPart = parsedText.parts[index - 1];
            const nextPart = parsedText.parts[index + 1];
            const hasPreviousHighlight = previousPart?.type === "highlight";
            const hasNextHighlight = nextPart?.type === "highlight";

            if (!hasPreviousHighlight && !hasNextHighlight) {
                return;
            }

            const hasMeaningfulContext = part.text.trim().length > 0;
            if (!hasMeaningfulContext) {
                if (hasPreviousHighlight && hasNextHighlight) {
                    copy.appendChild(document.createTextNode(" "));
                }
                return;
            }

            if (!hasPreviousHighlight && hasNextHighlight) {
                copy.append(
                    this.createCollapsedContextMarker("testimonial-block__collapsed-context-marker--prefix"),
                    document.createTextNode(" ")
                );
                return;
            }

            if (hasPreviousHighlight && !hasNextHighlight) {
                copy.append(
                    document.createTextNode(" "),
                    this.createCollapsedContextMarker("testimonial-block__collapsed-context-marker--suffix")
                );
                return;
            }

            copy.append(
                document.createTextNode(" "),
                this.createCollapsedContextMarker("testimonial-block__collapsed-context-marker--between"),
                document.createTextNode(" ")
            );
        });

        const closingQuotePlaceholder = document.createElement("span");
        closingQuotePlaceholder.className = "testimonial-block__closing-quote-placeholder testimonial-block__closing-quote-placeholder--collapsed";
        closingQuotePlaceholder.setAttribute("aria-hidden", "true");
        closingQuotePlaceholder.textContent = "\u201d";

        measure.append(copy, closingQuotePlaceholder);
        layer.appendChild(measure);
        return layer;
    }

    /**
     * Creates the moving overlay layer that carries one token per highlighted word and the animated closing quote.
     */
    private createHighlightMotionLayer(tokenizedHighlights: TokenizedHighlights): HTMLDivElement {
        const layer = document.createElement("div");
        layer.className = "testimonial-block__quote-stage-layer testimonial-block__quote-stage-layer--highlight-motion";
        layer.setAttribute("aria-hidden", "true");

        tokenizedHighlights.words.forEach((word) => {
            const token = document.createElement("span");
            token.className = "testimonial-block__motion-token";
            token.setAttribute("data-highlight-token-id", word.id);
            token.textContent = word.text;
            layer.appendChild(token);
        });

        const closingQuote = this.createDecorativeQuoteIcon("testimonial-block__motion-closing-quote testimonial-block__quote testimonial-block__quote--close");
        layer.appendChild(closingQuote);

        return layer;
    }

    /**
     * Creates the steady expanded layer with the full testimonial quote built from the tagged source text.
     */
    private createFullTextLayer(parsedText: ParsedTaggedText, tokenizedHighlights: TokenizedHighlights): HTMLDivElement {
        const layer = document.createElement("div");
        layer.className = "testimonial-block__quote-stage-layer testimonial-block__quote-stage-layer--full-text";
        layer.setAttribute("aria-hidden", "true");

        const measure = document.createElement("div");
        measure.className = "testimonial-block__quote-stage-measure";

        const copy = document.createElement("p");
        copy.className = "testimonial-block__quote-copy testimonial-block__quote-copy--full-text";
        parsedText.parts.forEach((part) => {
            if (part.type === "text") {
                copy.appendChild(document.createTextNode(part.text));
                return;
            }

            const highlightSegment = tokenizedHighlights.segments[part.highlightIndex];
            if (!highlightSegment) {
                return;
            }

            this.appendHighlightTokens(copy, highlightSegment, "testimonial-block__full-text-highlight-token");
        });

        const closingQuotePlaceholder = document.createElement("span");
        closingQuotePlaceholder.className = "testimonial-block__closing-quote-placeholder testimonial-block__closing-quote-placeholder--expanded";
        closingQuotePlaceholder.setAttribute("aria-hidden", "true");
        closingQuotePlaceholder.textContent = "\u201d";

        measure.append(copy, closingQuotePlaceholder);
        layer.appendChild(measure);
        return layer;
    }

    /**
     * Appends the tokenized highlight sequence to a quote copy container without using innerHTML.
     */
    private appendHighlightTokens(container: HTMLElement, highlightSegment: TokenizedHighlightSegment, tokenClassName: string): void {
        highlightSegment.words.forEach((word) => {
            if (word.leadingWhitespace) {
                container.appendChild(document.createTextNode(word.leadingWhitespace));
            }

            const span = document.createElement("span");
            span.className = tokenClassName;
            span.setAttribute("data-highlight-token-id", word.id);
            span.textContent = word.text;
            container.appendChild(span);
        });

        if (highlightSegment.trailingWhitespace) {
            container.appendChild(document.createTextNode(highlightSegment.trailingWhitespace));
        }
    }

    /**
     * Starts the expand sequence: height and token motion first, then the full-text fade-in.
     */
    private startExpandAnimation(parsedText: ParsedTaggedText): void {
        const measurement = this.measureQuoteStage(parsedText);
        if (!measurement) {
            this.completeSteadyStateTransition(true, parsedText);
            return;
        }

        const timing = this.readAnimationTiming();
        const expandMotionDurationMs = Math.max(
            timing.quoteStageHeightDurationMs,
            timing.highlightedWordMotionDurationMs,
            timing.closingQuoteMotionDurationMs
        );

        this.clearAnimationTimers();
        this.shellExpanded = true;
        this.fullTextVisibility = "hidden";
        this.collapsedContextVisibility = "fading-out";
        this.updateShellExpandedState();
        this.updateQuoteStageStateAttributes(parsedText);

        this.runAnimationStep(timing.collapsedContextFadeDurationMs, () => {
            this.animationState = "expanding";
            this.collapsedContextVisibility = "hidden";
            this.updateQuoteStageStateAttributes(parsedText);
            this.showMotionLayer();
            this.applyMotionLayerGeometry(measurement, "expanding");
            this.transitionQuoteStageHeight(measurement.collapsedHeight, measurement.expandedHeight);
            this.startMotionLayerTransition(measurement, "expanding");

            this.runAnimationStep(expandMotionDurationMs, () => {
                this.fullTextVisibility = "fading-in";
                this.updateQuoteStageStateAttributes(parsedText);
                this.emitTextUpdate();

                this.runAnimationStep(timing.fullTextFadeDurationMs, () => {
                    this.animationState = "expanded";
                    this.fullTextVisibility = "visible";
                    this.updateQuoteStageStateAttributes(parsedText);
                    this.synchronizeSteadyStateLayout(parsedText);
                    this.emitTextUpdate();
                });
            });
        });
    }

    /**
     * Starts the collapse sequence: fade the full text out first, then shrink the quote stage and move tokens back.
     */
    private startCollapseAnimation(parsedText: ParsedTaggedText): void {
        const measurement = this.measureQuoteStage(parsedText);
        if (!measurement) {
            this.completeSteadyStateTransition(false, parsedText);
            return;
        }

        const timing = this.readAnimationTiming();
        const collapseMotionDurationMs = Math.max(
            timing.quoteStageHeightDurationMs,
            timing.highlightedWordMotionDurationMs,
            timing.closingQuoteMotionDurationMs
        );

        this.clearAnimationTimers();
        this.animationState = "collapsing";
        this.fullTextVisibility = "fading-out";
        this.collapsedContextVisibility = "hidden";
        this.updateQuoteStageStateAttributes(parsedText);
        this.showMotionLayer();
        this.applyMotionLayerGeometry(measurement, "collapsing");

        this.runAnimationStep(timing.fullTextFadeDurationMs, () => {
            this.shellExpanded = false;
            this.fullTextVisibility = "hidden";
            this.updateShellExpandedState();
            this.updateQuoteStageStateAttributes(parsedText);
            this.emitTextUpdate();
            this.transitionQuoteStageHeight(measurement.expandedHeight, measurement.collapsedHeight);
            this.startMotionLayerTransition(measurement, "collapsing");

            this.runAnimationStep(collapseMotionDurationMs, () => {
                this.resetVisualStateToCollapsed("fading-in");
                this.updateQuoteStageStateAttributes(parsedText);
                this.synchronizeSteadyStateLayout(parsedText);
                this.emitTextUpdate();

                this.runAnimationStep(timing.collapsedContextFadeDurationMs, () => {
                    this.collapsedContextVisibility = "visible";
                    this.updateQuoteStageStateAttributes(parsedText);
                });
            });
        });
    }

    /**
     * Measures the collapsed and expanded quote-stage geometry that drives the local animation.
     */
    private measureQuoteStage(parsedText: ParsedTaggedText): QuoteStageMeasurement | null {
        const collapsedTokenRects = this.collectTokenRects(this.highlightRestLayer, "collapsed");
        const expandedTokenRects = this.collectTokenRects(this.fullTextLayer, "expanded");
        const collapsedHeight = this.measureLayerHeight(this.highlightRestLayer, parsedText.collapsedPreviewText);
        const expandedHeight = this.measureLayerHeight(this.fullTextLayer, parsedText.plainText);
        const collapsedClosingQuoteRect = this.measureClosingQuoteRect(this.highlightRestLayer, "collapsed", parsedText.collapsedPreviewText);
        const expandedClosingQuoteRect = this.measureClosingQuoteRect(this.fullTextLayer, "expanded", parsedText.plainText);

        if (
            !collapsedTokenRects ||
            !expandedTokenRects ||
            collapsedHeight === null ||
            expandedHeight === null ||
            !collapsedClosingQuoteRect ||
            !expandedClosingQuoteRect
        ) {
            return null;
        }

        return {
            collapsedHeight,
            expandedHeight,
            collapsedTokenRects,
            expandedTokenRects,
            collapsedClosingQuoteRect,
            expandedClosingQuoteRect
        };
    }

    /**
     * Collects per-token positions relative to the quote-stage viewport.
     */
    private collectTokenRects(layer: HTMLElement | null, layerState: "collapsed" | "expanded"): Map<string, RelativeRect> | null {
        const rects = new Map<string, RelativeRect>();
        if (!layer) {
            return null;
        }

        const tokens = Array.from(layer.querySelectorAll<HTMLElement>("[data-highlight-token-id]"));
        tokens.forEach((token, index) => {
            const id = token.getAttribute("data-highlight-token-id");
            if (!id) {
                rects.clear();
                return;
            }

            const rect = this.measureRelativeRect(token, index, layerState);
            if (!rect) {
                rects.clear();
                return;
            }

            rects.set(id, rect);
        });

        return rects.size === tokens.length ? rects : null;
    }

    /**
     * Measures a closing-quote placeholder so the actual quote icon can animate to the correct location.
     */
    private measureClosingQuoteRect(
        layer: HTMLElement | null,
        layerState: "collapsed" | "expanded",
        fallbackText: string
    ): RelativeRect | null {
        const placeholder = layer?.querySelector<HTMLElement>(".testimonial-block__closing-quote-placeholder");
        if (!placeholder) {
            return null;
        }

        return this.measureRelativeRect(
            placeholder,
            Math.max(0, fallbackText.trim().split(/\s+/).length - 1),
            layerState
        );
    }

    /**
     * Measures an element relative to the quote-stage viewport and falls back to a deterministic synthetic layout in jsdom.
     */
    private measureRelativeRect(element: HTMLElement, index: number, layerState: "collapsed" | "expanded"): RelativeRect | null {
        const viewportRect = this.quoteStageViewport?.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        if (
            viewportRect &&
            (elementRect.width > 0 || elementRect.height > 0 || elementRect.left !== 0 || elementRect.top !== 0)
        ) {
            return {
                left: elementRect.left - viewportRect.left,
                top: elementRect.top - viewportRect.top,
                width: elementRect.width,
                height: elementRect.height
            };
        }

        return this.getGeometryFallbackProvider()?.measureRelativeRect(element, index, layerState) || null;
    }

    /**
     * Measures a layer height and falls back to a deterministic estimate when the DOM environment has no layout engine.
     */
    private measureLayerHeight(layer: HTMLElement | null, fallbackText: string): number | null {
        if (!layer) {
            return null;
        }

        const measurementElement = layer.querySelector<HTMLElement>(".testimonial-block__quote-stage-measure") || layer;
        const boundingHeight = measurementElement.getBoundingClientRect().height;
        if (boundingHeight > 0) {
            return boundingHeight;
        }

        if (measurementElement.scrollHeight > 0) {
            return measurementElement.scrollHeight;
        }

        return this.getGeometryFallbackProvider()?.measureLayerHeight(layer, fallbackText) || null;
    }

    /**
     * Positions the motion layer at its source geometry before the CSS transitions begin.
     *
     * Guideline Exception: runtime geometry for testimonial animation has to be written inline because
     * the values are measured in pixels at interaction time and cannot be represented in static CSS alone.
     */
    private applyMotionLayerGeometry(measurement: QuoteStageMeasurement, direction: "expanding" | "collapsing"): void {
        if (!this.highlightMotionLayer) {
            return;
        }

        const motionTokens = Array.from(this.highlightMotionLayer.querySelectorAll<HTMLElement>(".testimonial-block__motion-token"));
        const startRects = direction === "expanding" ? measurement.collapsedTokenRects : measurement.expandedTokenRects;
        const endRects = direction === "expanding" ? measurement.expandedTokenRects : measurement.collapsedTokenRects;

        motionTokens.forEach((token) => {
            const tokenId = token.getAttribute("data-highlight-token-id") || "";
            const startRect = startRects.get(tokenId);
            const endRect = endRects.get(tokenId);
            if (!startRect || !endRect) {
                return;
            }

            token.style.width = `${startRect.width}px`;
            token.style.height = `${startRect.height}px`;
            token.style.transform = `translate(${startRect.left}px, ${startRect.top}px)`;
            token.setAttribute("data-target-transform", `translate(${endRect.left}px, ${endRect.top}px)`);
        });

        const closingQuote = this.highlightMotionLayer.querySelector<HTMLElement>(".testimonial-block__motion-closing-quote");
        const startClosingQuote = direction === "expanding" ? measurement.collapsedClosingQuoteRect : measurement.expandedClosingQuoteRect;
        const endClosingQuote = direction === "expanding" ? measurement.expandedClosingQuoteRect : measurement.collapsedClosingQuoteRect;

        if (closingQuote) {
            closingQuote.style.width = `${startClosingQuote.width}px`;
            closingQuote.style.height = `${startClosingQuote.height}px`;
            closingQuote.style.transform = `translate(${startClosingQuote.left}px, ${startClosingQuote.top}px)`;
            closingQuote.setAttribute("data-target-transform", `translate(${endClosingQuote.left}px, ${endClosingQuote.top}px)`);
        }
    }

    /**
     * Kicks off the CSS transform transitions for the motion layer after its source geometry is applied.
     */
    private startMotionLayerTransition(measurement: QuoteStageMeasurement, direction: "expanding" | "collapsing"): void {
        if (!this.highlightMotionLayer) {
            return;
        }

        const motionTokens = Array.from(this.highlightMotionLayer.querySelectorAll<HTMLElement>(".testimonial-block__motion-token"));
        const closingQuote = this.highlightMotionLayer.querySelector<HTMLElement>(".testimonial-block__motion-closing-quote");
        const steadyRects = direction === "expanding" ? measurement.expandedTokenRects : measurement.collapsedTokenRects;
        const steadyClosingQuote = direction === "expanding" ? measurement.expandedClosingQuoteRect : measurement.collapsedClosingQuoteRect;

        motionTokens.forEach((token) => {
            const targetTransform = token.getAttribute("data-target-transform");
            if (targetTransform) {
                token.style.transform = targetTransform;
            }
        });

        if (closingQuote) {
            const targetTransform = closingQuote.getAttribute("data-target-transform");
            if (targetTransform) {
                closingQuote.style.transform = targetTransform;
            }
        }

        if (direction === "expanding") {
            this.positionSteadyMotionGeometry(steadyRects, steadyClosingQuote);
        }
    }

    /**
     * Transitions the local quote-stage viewport height between the measured collapsed and expanded values.
     *
     * Guideline Exception: the quote-stage height is interaction-time geometry and therefore has to be applied inline.
     */
    private transitionQuoteStageHeight(fromHeight: number, toHeight: number): void {
        if (!this.quoteStageViewport) {
            return;
        }

        this.quoteStageViewport.style.height = `${fromHeight}px`;
        void this.quoteStageViewport.offsetHeight;
        this.quoteStageViewport.style.height = `${toHeight}px`;
    }

    /**
     * Synchronizes the quote stage to its steady geometry after rendering, resizing, or animation completion.
     *
     * Guideline Exception: steady-state height and icon geometry are measured pixel values and therefore
     * need inline updates after layout has settled.
     */
    private synchronizeSteadyStateLayout(parsedText: ParsedTaggedText): void {
        if (!this.quoteStageViewport || !this.highlightMotionLayer) {
            return;
        }

        const measurement = this.measureQuoteStage(parsedText);
        if (!measurement) {
            this.quoteStageViewport.style.height = "";
            this.hideMotionLayer();
            return;
        }

        const isExpandedState = this.animationState === "expanded";
        const targetHeight = isExpandedState ? measurement.expandedHeight : measurement.collapsedHeight;
        const targetTokenRects = isExpandedState ? measurement.expandedTokenRects : measurement.collapsedTokenRects;
        const targetClosingQuoteRect = isExpandedState ? measurement.expandedClosingQuoteRect : measurement.collapsedClosingQuoteRect;

        this.quoteStageViewport.style.height = `${targetHeight}px`;
        this.showMotionLayer();
        this.positionSteadyMotionGeometry(targetTokenRects, targetClosingQuoteRect);
    }

    /**
     * Updates the steady motion geometry so the closing quote always sits at the current logical target location.
     */
    private positionSteadyMotionGeometry(tokenRects: Map<string, RelativeRect>, closingQuoteRect: RelativeRect): void {
        if (!this.highlightMotionLayer) {
            return;
        }

        const motionTokens = Array.from(this.highlightMotionLayer.querySelectorAll<HTMLElement>(".testimonial-block__motion-token"));
        motionTokens.forEach((token) => {
            const tokenId = token.getAttribute("data-highlight-token-id") || "";
            const rect = tokenRects.get(tokenId);
            if (!rect) {
                return;
            }

            token.style.width = `${rect.width}px`;
            token.style.height = `${rect.height}px`;
            token.style.transform = `translate(${rect.left}px, ${rect.top}px)`;
        });

        const closingQuote = this.highlightMotionLayer.querySelector<HTMLElement>(".testimonial-block__motion-closing-quote");
        if (!closingQuote) {
            return;
        }

        closingQuote.style.width = `${closingQuoteRect.width}px`;
        closingQuote.style.height = `${closingQuoteRect.height}px`;
        closingQuote.style.transform = `translate(${closingQuoteRect.left}px, ${closingQuoteRect.top}px)`;
    }

    /**
     * Completes an expand/collapse request synchronously when measured geometry is unavailable.
     */
    private completeSteadyStateTransition(expanded: boolean, parsedText: ParsedTaggedText): void {
        this.clearAnimationTimers();
        this.shellExpanded = expanded;

        if (expanded) {
            this.animationState = "expanded";
            this.fullTextVisibility = "visible";
            this.collapsedContextVisibility = "hidden";
        } else {
            this.resetVisualStateToCollapsed("visible");
        }

        this.updateShellExpandedState();
        this.updateQuoteStageStateAttributes(parsedText);
        this.hideMotionLayer();

        if (this.quoteStageViewport) {
            this.quoteStageViewport.style.height = "";
        }

        this.emitTextUpdate();
    }

    /**
     * Returns the optional test-only geometry fallback provider.
     */
    private getGeometryFallbackProvider(): TestimonialGeometryFallbackProvider | null {
        return TestimonialBlockComponent.geometryFallbackProvider;
    }

    /**
     * Reveals the measured motion layer once valid geometry is available again.
     */
    private showMotionLayer(): void {
        if (!this.highlightMotionLayer) {
            return;
        }

        this.highlightMotionLayer.style.visibility = "";
    }

    /**
     * Hides the motion layer when the component cannot position it safely.
     */
    private hideMotionLayer(): void {
        if (!this.highlightMotionLayer) {
            return;
        }

        this.highlightMotionLayer.style.visibility = "hidden";
    }

    /**
     * Reads all animation timing values from CSS custom properties so CSS and TypeScript stay in sync.
     */
    private readAnimationTiming(): TestimonialAnimationTiming {
        const styles = getComputedStyle(this);

        return {
            quoteStageHeightDurationMs: parseCssDuration(styles.getPropertyValue("--testimonial-block-quote-stage-height-duration"), DEFAULT_TIMING.quoteStageHeightDurationMs),
            highlightedWordMotionDurationMs: parseCssDuration(styles.getPropertyValue("--testimonial-block-highlighted-word-motion-duration"), DEFAULT_TIMING.highlightedWordMotionDurationMs),
            fullTextFadeDurationMs: parseCssDuration(styles.getPropertyValue("--testimonial-block-full-text-fade-duration"), DEFAULT_TIMING.fullTextFadeDurationMs),
            closingQuoteMotionDurationMs: parseCssDuration(styles.getPropertyValue("--testimonial-block-closing-quote-motion-duration"), DEFAULT_TIMING.closingQuoteMotionDurationMs),
            collapsedContextFadeDurationMs: parseCssDuration(styles.getPropertyValue("--testimonial-block-collapsed-context-fade-duration"), DEFAULT_TIMING.collapsedContextFadeDurationMs)
        };
    }

    /**
     * Keeps local stage data attributes and semantic fallback text aligned with the visual state machine.
     */
    private updateQuoteStageStateAttributes(parsedText: ParsedTaggedText): void {
        if (!this.quoteStage) {
            return;
        }

        this.quoteStage.setAttribute("data-animation-state", this.animationState);
        this.quoteStage.setAttribute("data-full-text-visibility", this.fullTextVisibility);
        this.quoteStage.setAttribute("data-collapsed-context-visibility", this.collapsedContextVisibility);

        if (this.semanticText) {
            this.semanticText.textContent = this.fullTextVisibility === "hidden" ? parsedText.collapsedText : parsedText.plainText;
        }
    }

    /**
     * Reflects the currently accepted shell state back to the presentational wrapper.
     */
    private updateShellExpandedState(): void {
        if (!this.expandableShell) {
            return;
        }

        if (this.shellExpanded) {
            this.expandableShell.setAttribute("expanded", "");
        } else {
            this.expandableShell.removeAttribute("expanded");
        }
    }

    /**
     * Resets the logical and visual state machine to the steady collapsed state.
     */
    private resetVisualStateToCollapsed(collapsedContextVisibility: CollapsedContextVisibilityState): void {
        this.shellExpanded = false;
        this.animationState = "collapsed";
        this.fullTextVisibility = "hidden";
        this.collapsedContextVisibility = collapsedContextVisibility;
    }

    /**
     * Returns true while an expand or collapse choreography is still in progress.
     */
    private isAnimationInProgress(): boolean {
        return this.animationState === "expanding" ||
            this.animationState === "collapsing" ||
            this.collapsedContextVisibility === "fading-out" ||
            this.collapsedContextVisibility === "fading-in";
    }

    /**
     * Checks the user's reduced-motion preference and allows a synchronous completion path.
     */
    private prefersReducedMotion(): boolean {
        return typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    /**
     * Starts observing host resizes and batches steady-state sync into a single animation frame.
     */
    private startResizeObservation(): void {
        if (typeof ResizeObserver !== "function") {
            return;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        this.resizeObserver = new ResizeObserver(() => {
            this.scheduleResizeSync();
        });
        this.resizeObserver.observe(this);
    }

    /**
     * Stops resize observation and cancels any queued resize sync frame.
     */
    private stopResizeObservation(): void {
        this.cancelResizeSync();

        if (!this.resizeObserver) {
            return;
        }

        this.resizeObserver.disconnect();
        this.resizeObserver = null;
    }

    /**
     * Coalesces repeated resize notifications into one per animation frame.
     */
    private scheduleResizeSync(): void {
        if (this.resizeFrameId !== null) {
            return;
        }

        this.resizeFrameId = window.requestAnimationFrame(() => {
            this.resizeFrameId = null;
            this.handleResize();
        });
    }

    /**
     * Cancels a queued resize sync when the component disconnects or re-renders.
     */
    private cancelResizeSync(): void {
        if (this.resizeFrameId === null) {
            return;
        }

        window.cancelAnimationFrame(this.resizeFrameId);
        this.resizeFrameId = null;
    }

    /**
     * Recomputes steady-state geometry after viewport changes so the quote stage does not keep stale pixel measurements.
     */
    private handleResize(): void {
        const data = this.getTestimonialData();
        if (!data || this.isAnimationInProgress()) {
            return;
        }

        try {
            const parsedText = parseTaggedText(data.text);
            this.synchronizeSteadyStateLayout(parsedText);
        } catch {
            this.clearAnimationTimers();
            this.resetVisualStateToCollapsed("visible");
            this.updateShellExpandedState();
        }
    }

    /**
     * Creates one collapsed-only marker that makes the out-of-context quote treatment explicit.
     */
    private createCollapsedContextMarker(variantClassName: string): HTMLSpanElement {
        const marker = document.createElement("span");
        marker.className = `testimonial-block__collapsed-context-marker ${variantClassName}`;
        marker.setAttribute("aria-hidden", "true");
        marker.textContent = "[...]";
        return marker;
    }

    /**
     * Creates a decorative quote icon for the testimonial quote stage.
     */
    private createDecorativeQuoteIcon(className: string): HTMLElement {
        const icon = document.createElement("jt-icon");
        icon.setAttribute("icon", "quote");
        icon.setAttribute("aria-hidden", "true");
        icon.className = className;
        return icon;
    }

    /**
     * Tracks an animation timer so it can be cancelled cleanly if the testimonial is re-rendered mid-flight.
     */
    private registerAnimationTimer(callback: () => void, delayMs: number): void {
        const timerId = window.setTimeout(() => {
            this.animationTimerIds.delete(timerId);
            callback();
        }, delayMs);

        this.animationTimerIds.add(timerId);
    }

    /**
     * Runs an animation step immediately for zero-duration timings and otherwise tracks it as a cancellable timer.
     */
    private runAnimationStep(delayMs: number, callback: () => void): void {
        if (delayMs <= 0) {
            callback();
            return;
        }

        this.registerAnimationTimer(callback, delayMs);
    }

    /**
     * Cancels all queued animation timers.
     */
    private clearAnimationTimers(): void {
        this.animationTimerIds.forEach((timerId) => window.clearTimeout(timerId));
        this.animationTimerIds.clear();
    }

    /**
     * Clears cached quote-stage element references after the quote body is removed.
     */
    private clearQuoteStageReferences(): void {
        this.quoteStage = null;
        this.quoteStageViewport = null;
        this.highlightRestLayer = null;
        this.highlightMotionLayer = null;
        this.fullTextLayer = null;
        this.semanticText = null;
    }

    /**
     * Emits a bubbling text-update event so the parent participant message list can recompute its full-text mirror.
     */
    private emitTextUpdate(): void {
        this.dispatchEvent(new CustomEvent("text-update", { bubbles: true }));
    }
}

if (!customElements.get("jt-testimonial-block")) {
    customElements.define("jt-testimonial-block", TestimonialBlockComponent);
}
